import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import BackButton from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import api, { errMsg } from "@/lib/api";
import { PLAN_META } from "@/lib/constants";
import Recorder from "@/components/Recorder";
import { compressVideo } from "@/lib/videoCompress";
import { Upload as UploadIcon, Film, Loader2, Zap, Crown, Video, FileVideo, Mic } from "lucide-react";

export default function Upload() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const challengeTo = params.get("challenge");
  const [copyrightOk, setCopyrightOk] = useState(false);
  const [mode, setMode] = useState("upload"); // 'upload' | 'record' | 'voice'
  const [file, setFile] = useState(null);
  const [duration, setDuration] = useState(0);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [progress, setProgress] = useState(0);
  const [challengedVideo, setChallengedVideo] = useState(null);
  const videoRef = useRef(null);

  const plan = PLAN_META[user?.plan || "free"];
  const maxDur = plan.dur;

  // Auto-select 'voice' mode for voice-first categories (Msg 112 request)
  useEffect(() => {
    if (["voice_artist", "singer", "poet"].includes(user?.category)) {
      setMode("voice");
    }
  }, [user?.category]);

  useEffect(() => {
    if (challengeTo) {
      api.get(`/videos/${challengeTo}`).then(r => setChallengedVideo(r.data)).catch(() => {});
    }
  }, [challengeTo]);

  useEffect(() => () => { if (preview) try { URL.revokeObjectURL(preview); } catch {} }, [preview]);

  const setSelectedFile = (f) => {
    if (!f) { setFile(null); setPreview(""); setDuration(0); return; }
    const isVid = f.type.startsWith("video/");
    const isAud = f.type.startsWith("audio/");
    if (!isVid && !isAud) { toast.error("Please select a video or audio file"); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setDuration(0);
  };

  const onLoadedMeta = () => {
    if (!videoRef.current) return;
    const d = Math.round(videoRef.current.duration);
    setDuration(d);
    if (d > maxDur) {
      toast.error(`Clip is ${d}s. Max ${maxDur}s on ${plan.name} plan.`);
    }
  };

  const onRecordDone = (recordedFile, dur) => {
    if (!recordedFile) { setMode("upload"); return; }
    setSelectedFile(recordedFile);
    if (dur) setDuration(dur);
    setMode("upload"); // fall through to preview/publish step
  };

  const submit = async () => {
    if (!file || !duration) { toast.error("Add a performance video"); return; }
    if (duration > maxDur) { toast.error(`Max ${maxDur}s on ${plan.name} plan`); return; }
    setBusy(true);
    setProgress(5);
    let outFile = file;
    const isAudio = (file.type || "").startsWith("audio/");
    try {
      // Auto-compress if file is over 15MB (only for videos)
      if (!isAudio && file.size > 15 * 1024 * 1024) {
        setStatus("Auto-compressing…");
        try {
          const compressed = await compressVideo(file, { maxDim: 720, targetBitrate: 1_200_000, onProgress: (p) => setProgress(5 + Math.round(p * 40)) });
          if (compressed && compressed.size < file.size) {
            outFile = compressed;
            toast.success(`Compressed to ${(compressed.size / 1024 / 1024).toFixed(1)} MB`);
          }
        } catch {}
      }
      setStatus("Uploading…");
      const fd = new FormData();
      fd.append("file", outFile);
      fd.append("title", title);
      fd.append("description", desc);
      fd.append("duration", String(duration));
      fd.append("kind", isAudio ? "audio" : "video");
      if (challengeTo) fd.append("challenge_to", challengeTo);
      const { data } = await api.post("/videos/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => setProgress(Math.min(98, 45 + Math.round((evt.loaded / (evt.total || 1)) * 50))),
      });
      setProgress(100);
      toast.success("Performance published!");
      nav(`/v/${data.id}`);
    } catch (err) {
      toast.error(errMsg(err, "Upload failed"));
    } finally {
      setBusy(false);
      setStatus("");
    }
  };

  return (
    <div data-testid="upload-page" className="min-h-screen pb-24">
      <TopBar subtitle={challengeTo ? "Challenge" : "Upload performance"}/>
      <div className="max-w-2xl mx-auto px-4 pt-4 space-y-4">
        <BackButton fallback="/app" label="Back"/>

        <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-zinc-400">Your plan</p>
              <p className={`font-display text-2xl font-bold ${plan.color}`}>{plan.name}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-zinc-400">Max duration</p>
              <p className="font-semibold text-white">{plan.dur}s</p>
              <p className="text-xs text-zinc-400 mt-1">{plan.videos}/month</p>
            </div>
          </div>
          {user?.plan === "free" && (
            <Link to="/app/plans" data-testid="upgrade-inline" className="mt-3 inline-flex items-center gap-2 text-xs text-[#DC2626] hover:underline">
              <Crown size={12}/> Upgrade for longer performances and more slots
            </Link>
          )}
        </div>

        {challengeTo && challengedVideo && (
          <div data-testid="challenge-context" className="rounded-2xl border border-[#DC2626]/40 bg-[#DC2626]/5 p-4">
            <div className="flex items-center gap-2 text-[#DC2626] text-xs">
              <Zap size={14}/> Challenging
            </div>
            <p className="mt-1 text-sm text-white">{challengedVideo.title || "Untitled"} by @{challengedVideo.username}</p>
          </div>
        )}

        {!file && (
          <div className="grid grid-cols-3 gap-2">
            <button data-testid="mode-upload" onClick={() => setMode("upload")} className={`h-11 rounded-full border text-sm font-semibold transition-colors ${mode === "upload" ? "bg-[#DC2626] border-[#DC2626] text-white" : "bg-zinc-900 border-zinc-700 text-zinc-200 hover:border-[#DC2626]/50"}`}>
              <FileVideo size={14} className="inline mr-1"/> Upload
            </button>
            <button data-testid="mode-record" onClick={() => setMode("record")} className={`h-11 rounded-full border text-sm font-semibold transition-colors ${mode === "record" ? "bg-[#DC2626] border-[#DC2626] text-white" : "bg-zinc-900 border-zinc-700 text-zinc-200 hover:border-[#DC2626]/50"}`}>
              <Video size={14} className="inline mr-1"/> Record
            </button>
            <button data-testid="mode-voice" onClick={() => setMode("voice")} className={`h-11 rounded-full border text-sm font-semibold transition-colors ${mode === "voice" ? "bg-[#DC2626] border-[#DC2626] text-white" : "bg-zinc-900 border-zinc-700 text-zinc-200 hover:border-[#DC2626]/50"}`}>
              <Mic size={14} className="inline mr-1"/> Voice
            </button>
          </div>
        )}

        {!file && mode === "record" && (
          <Recorder maxDur={maxDur} onDone={onRecordDone} onCancel={() => setMode("upload")}/>
        )}
        {!file && mode === "voice" && (
          <Recorder maxDur={maxDur} audioOnly onDone={onRecordDone} onCancel={() => setMode("upload")}/>
        )}

        {!file && mode === "upload" && (
          <label data-testid="file-drop" htmlFor="videofile" className="block cursor-pointer rounded-2xl border-2 border-dashed border-zinc-700 hover:border-[#DC2626]/50 transition-colors p-8 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#DC2626]/15 flex items-center justify-center text-[#DC2626] mb-3">
              <UploadIcon size={22}/>
            </div>
            <p className="font-semibold text-white">Tap to pick a video</p>
            <p className="text-xs text-zinc-400 mt-1">MP4, MOV or WebM · up to {plan.dur}s · auto-compressed on upload</p>
            <input id="videofile" data-testid="video-input" type="file" accept="video/*" className="hidden" onChange={(e) => setSelectedFile(e.target.files?.[0])}/>
          </label>
        )}

        {file && (
          <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5 space-y-4">
            <div className="flex items-center justify-between text-sm text-zinc-200">
              <span className="flex items-center gap-2 truncate"><Film size={14} className="text-[#DC2626]"/> {file.name} · {duration || "?"}s · {(file.size / 1024 / 1024).toFixed(1)} MB</span>
              <button data-testid="reset-file" onClick={() => setSelectedFile(null)} className="text-xs text-zinc-300 hover:text-white underline">Choose another</button>
            </div>

            {file.type.startsWith("audio/") ? (
              <audio ref={videoRef} data-testid="upload-preview" src={preview} onLoadedMetadata={onLoadedMeta} controls className="w-full"/>
            ) : (
              <video ref={videoRef} data-testid="upload-preview" src={preview} onLoadedMetadata={onLoadedMeta} controls className="w-full rounded-xl bg-black max-h-[50vh]"/>
            )}

            <div>
              <Label className="text-zinc-200 text-xs">Title</Label>
              <Input data-testid="upload-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Give your performance a title" className="mt-1 bg-zinc-900 border-zinc-700"/>
            </div>
            <div>
              <Label className="text-zinc-200 text-xs">Description</Label>
              <Textarea data-testid="upload-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What is this piece about?" className="mt-1 bg-zinc-900 border-zinc-700 min-h-[80px]"/>
            </div>

            {busy && (
              <div>
                <Progress value={progress} className="h-2 bg-zinc-800 [&>div]:bg-[#DC2626]"/>
                <p className="text-xs text-zinc-300 mt-2">{status}</p>
              </div>
            )}

            {/* Copyright guard — mandatory before publish */}
            <label className="flex items-start gap-2 rounded-xl border border-[#F5C462]/40 bg-[#F5C462]/5 p-3 cursor-pointer">
              <input data-testid="copyright-consent" type="checkbox" checked={copyrightOk} onChange={(e) => setCopyrightOk(e.target.checked)} className="mt-0.5 accent-[#DC2626] w-4 h-4"/>
              <span className="text-[11px] text-zinc-200 leading-relaxed">
                I confirm this performance is my original work or I have permission to share it.
              </span>
            </label>

            <Button data-testid="upload-submit" disabled={busy || !file || duration > maxDur || duration === 0 || !copyrightOk} onClick={submit} className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold disabled:opacity-40">
              {busy ? <><Loader2 className="animate-spin mr-2" size={16}/> Publishing…</> : "Publish"}
            </Button>
            <p className="text-[11px] text-zinc-400 text-center">Deleting a performance does not free your monthly slot.</p>
          </div>
        )}
      </div>
    </div>
  );
}
